import { Box, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useQuery } from "@tanstack/react-query";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import type { GetAllInput, WhereFilter } from "@types_def/common/filter.types";
import type { QueryType } from "@types_def/common/query.types";
import type Driver from "@types_def/models/driver.types";
import { driverTableRoutes } from "@utils/constants/DriverTable/driverTableRoutes";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import getAllFiltered from "@/api/graphql/generic/get-all-filtered";
import { downloadDrivers } from "@/api/Rest/download/driversDownloadXLSX";
import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";

import type { DriverTableRouteKey } from "./columns";

import { DRIVER_TABLE_COLUMNS_BY_ROUTE } from "./columns";

type Params = {
  tableRoute: DriverTableRouteKey;
};

function getFilterFromParams(params: DriverTableRouteKey): WhereFilter<Driver> {
  switch (params) {
    case "accepted-requests":
      return { confirmedByAlb: true, disabled: false, rejectedByAlb: false };
    case "refused-requests":
      return { rejectedByAlb: true, confirmedByAlb: false, disabled: false };
    case "disabled-drivers":
      return { disabled: true, confirmedByAlb: true };
    default:
      return { confirmedByAlb: false, rejectedByAlb: false, disabled: false };
  }
}

function validateFrenchPostalCode(code: string): boolean {
  if (code === "") return true;

  if (code.length === 2) {
    if (!/^\d+$/.test(code)) return false;

    const department = Number.parseInt(code, 10);
    return (department >= 1 && department <= 95) || (department >= 97 && department <= 97);
  }

  if (code.length !== 5) return false;

  if (!/^\d+$/.test(code)) return false;

  const department = Number.parseInt(code.substring(0, 2), 10);
  return (department >= 1 && department <= 95) || (department >= 971 && department <= 976);
}

export default function DriversTable() {
  "use no memo";
  const [searchParams, setSearchParams] = useSearchParams();
  const { tableRoute: tableRouteParam } = useParams<Params>();

  const tableRoute: DriverTableRouteKey =
    tableRouteParam && tableRouteParam in driverTableRoutes ? tableRouteParam : "waiting-requests";

  const page = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;
  const orderBy = (searchParams.get("orderBy") as keyof Driver | null) ?? "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";

  const emailSearch = searchParams.get("email") ?? "";
  const nameSearch = searchParams.get("nameSearch") ?? "";
  const postalCodeSearch = searchParams.get("postalCode") ?? "";

  const postalCodeError = useMemo(() => {
    if (postalCodeSearch === "") return "";

    return validateFrenchPostalCode(postalCodeSearch) ? "" : "Invalid French postal code";
  }, [postalCodeSearch]);

  const columns = useMemo(() => DRIVER_TABLE_COLUMNS_BY_ROUTE[tableRoute], [tableRoute]);

  const externalColumnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];

    if (emailSearch !== "") {
      filters.push({ id: "email", value: emailSearch });
    }

    if (nameSearch !== "") {
      filters.push({ id: "nameSearch", value: nameSearch });
    }

    if (postalCodeSearch !== "") {
      filters.push({ id: "postalCode", value: postalCodeSearch });
    }

    return filters;
  }, [emailSearch, nameSearch, postalCodeSearch]);

  const filter = useMemo<WhereFilter<Driver>>(() => {
    const baseFilter = getFilterFromParams(tableRoute);
    const mergedFilter: WhereFilter<Driver> = { ...baseFilter };

    if (emailSearch !== "") {
      mergedFilter.email = { contains: emailSearch };
    }

    if (nameSearch !== "") {
      const nameParts = nameSearch.trim().split(/\s+/).filter(Boolean);
      if (nameParts.length > 0) {
        mergedFilter.AND = nameParts.map((term) => ({
          OR: [{ firstName: { contains: term } }, { lastName: { contains: term } }],
        }));
      }
    }

    if (postalCodeSearch !== "" && postalCodeError === "") {
      mergedFilter.address =
        postalCodeSearch.length === 2
          ? { postalCode: { startsWith: postalCodeSearch } }
          : { postalCode: { contains: postalCodeSearch } };
    }

    return mergedFilter;
  }, [tableRoute, emailSearch, nameSearch, postalCodeSearch, postalCodeError]);

  const queryFields: QueryType<Driver> = {
    id: true,
    publicId: true,
    fullName: true,
    firstName: true,
    lastName: true,
    phoneNumber: true,
    drivingPermitNumber: true,
    totalRating: true,
    address: {
      city: true,
      postalCode: true,
    },
    createdAt: true,
    nbDeals: true,
    disabled: true,
  };

  const queryInput = useMemo<GetAllInput<Driver, keyof Driver>>(
    () => ({
      keyword: "",
      pagination: { page, perPage: pageSize },
      sort: { field: orderBy, order },
      filter,
    }),
    [page, pageSize, orderBy, order, filter]
  );

  const { data, isPending, isError } = useQuery({
    queryKey: ["drivers-table", tableRoute, queryInput],
    queryFn: async () => {
      const response = await getAllFiltered<Driver, keyof Driver>(
        queryInput,
        "Drivers",
        queryFields
      );
      return response.data;
    },
  });

  const pageCount = Math.ceil((data?.total ?? 0) / pageSize);

  const handlePaginationChange = (pagination: PaginationState) => {
    const nextParams = new URLSearchParams(searchParams);

    if (pagination.pageIndex > 0) {
      nextParams.set("page", `${pagination.pageIndex}`);
    } else {
      nextParams.delete("page");
    }

    if (pagination.pageSize !== 10) {
      nextParams.set("pageSize", String(pagination.pageSize));
    } else {
      nextParams.delete("pageSize");
    }

    setSearchParams(nextParams);
  };

  const handleSortingChange = (sorting: SortingState) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", String(nextSort.id));
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };

  const handleColumnFiltersChange = (filters: ColumnFiltersState) => {
    const nextParams = new URLSearchParams(searchParams);

    nextParams.delete("page");

    const getTextFilter = (id: string) => {
      const item = filters.find((filterItem) => filterItem.id === id);
      return typeof item?.value === "string" ? item.value : "";
    };

    const emailValue = getTextFilter("email").trim();
    const nameValue = getTextFilter("nameSearch").trim();
    const postalValue = getTextFilter("postalCode").trim();

    if (emailValue !== "") nextParams.set("email", emailValue);
    else nextParams.delete("email");

    if (nameValue !== "") nextParams.set("nameSearch", nameValue);
    else nextParams.delete("nameSearch");

    if (postalValue !== "") nextParams.set("postalCode", postalValue);
    else nextParams.delete("postalCode");

    setSearchParams(nextParams);
  };

  const { table } = useDataTable<Driver>({
    data: data?.records ?? [],
    columns,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    externalPagination: { pageIndex: page, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    externalColumnFilters,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    initialState: {
      columnVisibility: {
        email: false,
        nameSearch: false,
        postalCode: false,
      },
    },
  });

  const exportInput: GetAllInput<Driver, "id"> = {
    filter,
    pagination: { page, perPage: pageSize },
    sort: { field: "id", order },
  };

  return (
    <Box sx={{ p: 4 }}>
      <DataTable table={table} totalCount={data?.total ?? 0}>
        <DataTableToolbar table={table}>
          <Button onClick={() => downloadDrivers(exportInput)} size="small" variant="outlined">
            Export
          </Button>
        </DataTableToolbar>
      </DataTable>

      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        {isPending ? <Typography variant="body2">Loading...</Typography> : null}
        {isError ? (
          <Typography color="error" variant="body2">
            Failed to load drivers.
          </Typography>
        ) : null}
      </Stack>

      {postalCodeError !== "" ? (
        <Typography color="error" sx={{ mt: 1 }} variant="caption">
          {postalCodeError}
        </Typography>
      ) : null}
    </Box>
  );
}
